import { useEffect, useState } from 'react';
import { canDialLead } from '../../lib/demoData/demoGuard';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { MessageSquare, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { Spinner } from '../ui/Spinner';
import { LeadOutcomePicker } from './LeadOutcomePicker';
import { LeadStory } from './LeadStory';
import { StageChip, TemperatureDot } from './StageChip';
import { CallLeadPanel } from './CallLeadPanel';
import { TextLeadComposer } from './TextLeadComposer';
import { sourceLabel } from '../../lib/leads/sourceLabels';
import { SENT_TO_CRM_LABEL } from '../../lib/leads/relayOnly';
import { clubsMatching, scopeOptions } from '../../lib/leads/clubScope';
import { emptyListMessage, initialsOf, SPLIT_PERIODS, splitRangeFor, type SplitPeriodId } from '../../lib/leads/leadSplit';
import { ageShort, firstNameOf, firstResponseSentence, leadLine, stageOf, STAGE_STYLES, whereItsAt, type LeadStageKey } from '../../lib/leads/leadView';
import { botNameOf } from '../../lib/botName';
import { CONTACT_REASON_TEXT } from '../../lib/leads/contactState';
import { LEAD_QUALITIES, LEAD_QUALITY_LABELS, type LeadQuality } from '../../types/lead';
import type { LeadOutcome } from '../../types/leadOutcome.js';
import {
  LEAD_ROW_NOTICE_LABELS,
  LEAD_STAGE_LABELS,
  LEAD_STAGE_TABS,
  REACHED_BY,
  type LeadCallOptions,
  type LeadSplitDetail,
  type LeadSplitFilters,
  type LeadSplitPage,
  type LeadSplitRow,
  type LeadStageTab,
  type ReachedBy,
  type StaffLeadClub,
} from '../../types/leadSplit';
import { DISTRESS_BANNER, dayLabelFor, timeLabelFor } from '../../lib/leadBot/portalPageView';
import type { LeadHistoryEntry, LeadHistoryPage } from '../../lib/speedToLead/leadHistory';
import type { CallLogRow } from '../../types/leadCall';

// THE SHARED LEADS SPLIT VIEW (KyckConnect redesign Phase 2, Part B): ONE component in three places (the staff all-clients page, an
// account's Leads tab, the club portal) and in two looks. It wears SEMANTIC TOKENS only (bg-card, text-foreground, border-border,
// …) and `kc-*` hooks, so the staff app keeps today's styling and the portal wraps it in `data-kc-theme="kyck"` (src/index.css).
// ⚠️ THIS FILE NEVER NAMES A THEME: the host decides the look. It knows NO endpoint: the host passes a `LeadSplitApi` (the
// portalAssets "one grid, two hosts" device), and a source guard pins that neither host imports the other's api file.
//
// ⚠️ EVERYTHING IS A SERVER QUERY (filters, stage counts, search, paging). Nothing is filtered in the browser. ⚠️ The list shows the
// last four phone digits; the FULL number is in the detail header only (a `tel:` link), never in a URL or a log. ⚠️ ONE outcome
// control on the page: the picker in the outcome bar writes the LEAD's outcome; a call inside the history shows its AI
// suggestion as text only. ⚠️ No model is called from here: a suggestion is pre-selected and saved only when a person confirms.
// ⚠️ REFRESH: the list and the open lead refetch on focus and every 30 s with the previous data kept on screen, so the selected lead
// never flickers; the History panel takes the refreshed first page in place and keeps the older pages already loaded.

export interface LeadSplitApi {
  list: (filters: LeadSplitFilters, cursor: string | null, accountId: string | null) => Promise<LeadSplitPage>;
  detail: (leadId: string, cursor?: string | null) => Promise<LeadSplitDetail>;
  setOutcome: (leadId: string, code: LeadOutcome) => Promise<unknown>;
  setNeedsPerson: (leadId: string, on: boolean) => Promise<unknown>;
  loadPlayUrl: (kind: CallLogRow['kind'], id: string) => Promise<string>;
  /** Leads centre v2: the call chooser's content, and starting the call. Fetched only when a person presses Call. */
  callOptions: (leadId: string) => Promise<LeadCallOptions>;
  startCall: (leadId: string, teamMemberId: string) => Promise<unknown>;
  /** Leads centre v2, Part C: a person's text to the lead, and the Sam switch (true = a person has the texts). Used only on a click. */
  sendText: (leadId: string, text: string) => Promise<unknown>;
  setSamPaused: (leadId: string, paused: boolean) => Promise<unknown>;
}

export interface LeadSplitViewProps {
  api: LeadSplitApi;
  queryKey: readonly unknown[];
  /** A fixed club (an account's Leads tab). Null/absent = every club the viewer may see, and the club filter appears when `clubs` is given. */
  accountId?: string | null;
  clubs?: ReadonlyArray<{ id: string; name: string }>;
  /**
   * STAFF ONLY (the all-clients page): the clubs with their brand and group. Gives the Brand and Group filters, and they narrow the
   * Club list and the query scope. ⚠️ The portal never passes this, and its server schema refuses brandId / groupId.
   */
  staffClubs?: ReadonlyArray<StaffLeadClub>;
  /** Staff only: the "Show test leads" switch. The portal never passes it, and its server refuses the filter. */
  showTestToggle?: boolean;
  /** Staff only: where "Open in Approvals" goes. Absent (the portal) = the badge reads "Waiting for Kyck to approve". */
  approvalsHref?: string | null;
  initialStage?: LeadStageTab;
  /** The club's name for the page subtitle when it is not in `clubs` (an account's Leads tab). */
  scopeLabel?: string | null;
  /** Opens the Filters panel at first (a deep link, and the server-rendered tests). */
  initialFiltersOpen?: boolean;
}

export const REFRESH_MS = 30_000;
const SEARCH_DEBOUNCE_MS = 300;

const field = 'kc-field rounded border border-border bg-background px-2 py-1 text-sm text-foreground';
const label = 'kc-label block text-xs text-muted-foreground';

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// ── the list ────────────────────────────────────────────────────────────────────────────────────────────────

/** The facts the plain-English line reads, from a list row. */
const lineFactsOf = (row: LeadSplitRow) => ({
  outcome: row.outcome,
  outcomeSetBy: row.outcomeSetBy,
  needsPerson: row.needsPerson,
  contactStatus: row.contactStatus,
  distress: row.distress,
  assistantName: row.assistantName,
  services: row.services,
  latestKind: row.latestActivity?.kind ?? null,
  staffFirstName: row.staffFirstName,
  booking: row.booking,
  relayOnly: row.relayOnly,
});

function ListRow({ row, selected, onSelect, nowMs }: { row: LeadSplitRow; selected: boolean; onSelect: () => void; nowMs: number }) {
  const stage = stageOf(row);
  const line = leadLine(lineFactsOf(row));
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        data-testid="lead-row"
        data-selected={selected ? 'true' : 'false'}
        data-distress={row.distress ? 'true' : 'false'}
        className={`kc-row flex w-full items-start gap-3 border-b border-border px-3 py-3 text-left ${selected ? 'bg-accent' : row.distress ? 'bg-destructive/5 hover:bg-destructive/10' : 'hover:bg-muted/50'}`}
      >
        <span
          aria-hidden
          data-stage={stage}
          style={{ '--kc-stage-fill': STAGE_STYLES[stage].portalFill ?? 'transparent' } as React.CSSProperties}
          className="kc-avatar flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-semibold text-background"
        >
          {row.initials}
        </span>
        <span className="min-w-0 flex-1 space-y-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="kc-body truncate text-sm font-semibold text-foreground" data-testid="lead-name-row">
              {row.name ?? 'Unnamed lead'}
              {row.isTest && <span className="ml-2 text-xs font-normal text-muted-foreground" data-testid="lead-test-badge">Test</span>}
              {row.seeded && <span className="ml-2 rounded border px-1 text-xs font-normal text-muted-foreground" data-testid="lead-seeded-badge" title="Synthetic demo data, not a real enquiry">Demo data</span>}
            </span>
            <span className="kc-label shrink-0 text-xs text-muted-foreground" data-testid="lead-received">
              {ageShort(row.receivedAt, nowMs)}
            </span>
          </span>
          {row.accountName && (
            <span className="block truncate text-xs text-muted-foreground" data-testid="lead-club">
              {row.accountName}
            </span>
          )}
          <span className="kc-body block text-sm text-muted-foreground" data-testid="lead-activity">
            {row.notice ? `${LEAD_ROW_NOTICE_LABELS[row.notice]} · ` : ''}{line}
          </span>
          <span className="flex flex-wrap items-center gap-2">
            <StageChip stage={stage} testId="lead-stage" />
            {row.distress ? <StageChip stage="distress" testId="lead-distress" /> : <TemperatureDot quality={row.quality} testId="lead-quality" />}
          </span>
        </span>
      </button>
    </li>
  );
}

// ── the detail ──────────────────────────────────────────────────────────────────────────────────────────────

const when = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

/** The heads-up notices the club sees before anything else on a lead (carried over from the old portal conversation panel). */
export function LeadNoticesBlock({ d }: { d: LeadSplitDetail }) {
  const n = d.notices;
  const apt = n.appointment;
  if (!n.distress && !n.handoff && !n.optedOut && !apt) return null;
  return (
    <div className="space-y-2" data-testid="lead-notices">
      {n.distress && (
        <p role="alert" className="rounded-md border-2 border-destructive px-3 py-2 text-sm font-medium text-destructive" data-testid="distress-banner">
          {DISTRESS_BANNER} Their number: {d.phone}
        </p>
      )}
      {n.handoff && (
        <p role="status" className="kc-body rounded-md border border-border px-3 py-2 text-sm text-foreground" data-testid="handoff-notice">
          {d.assistantName ?? 'The assistant'} has handed this to your team{n.handoff.reasons.length > 0 ? `: ${n.handoff.reasons.join(', ')}` : ''}.
        </p>
      )}
      {n.optedOut && (
        <p role="status" className="kc-body rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground" data-testid="optedout-notice">
          This person opted out of text messages, so nothing can be sent to them.
        </p>
      )}
      {apt && (
        <p className="kc-body text-sm" data-testid="lead-booking">
          <span className="text-muted-foreground">Booked: </span>
          <span className="font-medium text-foreground">{dayLabelFor(apt.startsAt, apt.timezone)} at {timeLabelFor(apt.startsAt, apt.timezone)}</span>
        </p>
      )}
    </div>
  );
}

/**
 * THE SAM STRIP: whose hands the texts are in, with a real switch. "Sam on" = the assistant is texting; paused = a person has the texts.
 * The switch replaces the old Take over / Hand back buttons and calls the SAME endpoint (`setSamPaused`, true = a person has the texts).
 * ⚠️ It needs the permission AND a conversation to switch: without the permission it still SHOWS the state but is disabled, with the reason;
 * the server checks both again. Nothing is called until a person presses it.
 */
function SamStrip({ d, api, refresh }: { d: LeadSplitDetail; api: Pick<LeadSplitApi, 'setSamPaused'>; refresh: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!d.sam.available) return null;
  const bot = botNameOf(d.assistantName);
  const botTitle = bot === 'the assistant' ? 'The assistant' : bot;
  const first = firstNameOf(d.name);
  const noPermission = d.contact.text?.code === 'no_permission';
  const reason = noPermission ? CONTACT_REASON_TEXT.no_permission.text : !d.sam.hasConversation ? 'There are no texts with this lead yet.' : null;
  const on = !d.sam.paused;
  const flip = async () => {
    if (reason !== null || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.setSamPaused(d.id, !d.sam.paused);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="kc-sam kc-card flex flex-wrap items-center gap-3 rounded border border-border bg-card px-3 py-3" aria-label="AI SMS" data-testid="sam-strip" data-sam={d.sam.paused ? 'paused' : 'on'}>
      <span aria-hidden className="kc-sam-icon flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground text-background">
        <MessageSquare size={16} strokeWidth={1.5} />
      </span>
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="kc-body text-sm font-semibold text-foreground" data-testid="sam-strip-text">
          {d.sam.paused ? 'Your team has the texts' : `${botTitle} is texting ${first}`}
        </p>
        <p className="kc-body text-sm text-muted-foreground" data-testid="sam-strip-help">
          {d.sam.paused
            ? `${d.sam.pausedByName ? `${d.sam.pausedByName} took over. ` : ''}${botTitle} won't reply until you hand back.`
            : `${botTitle} replies to ${first}'s texts automatically. Pause ${botTitle === 'The assistant' ? 'it' : botTitle} to take over the texts yourself.`}
        </p>
        {reason && <p className="kc-body text-xs text-muted-foreground" data-testid="sam-switch-reason">{reason}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={`${botTitle} ${on ? 'on' : 'paused'}`}
        disabled={reason !== null || busy}
        onClick={() => void flip()}
        className="kc-switch flex shrink-0 items-center gap-2 rounded-full border border-border px-2 py-1 disabled:opacity-60"
        data-testid="sam-switch"
      >
        <span className="kc-label text-xs text-foreground" data-testid="sam-switch-label">{busy ? 'Saving…' : on ? `${botTitle} on` : `${botTitle} paused`}</span>
        <span aria-hidden className="kc-switch-track relative inline-block h-5 w-9 rounded-full" data-on={on ? 'true' : 'false'}>
          <span className="kc-switch-knob absolute top-0.5 h-4 w-4 rounded-full bg-background" style={{ left: on ? '1.125rem' : '0.125rem' }} />
        </span>
      </button>
      {error && <p role="alert" className="kc-body w-full text-sm text-destructive" data-testid="sam-switch-error">{error}</p>}
    </section>
  );
}

/** The older pages of the history, loaded on "Show older" and kept across the host's 30-second refresh of page one. */
function useStoryEntries(first: LeadHistoryPage, load: (cursor: string | null) => Promise<LeadHistoryPage>) {
  const [older, setOlder] = useState<LeadHistoryEntry[]>([]);
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next = cursor === undefined ? first.nextCursor : cursor;
  const showOlder = async () => {
    if (!next) return;
    setBusy(true);
    setError(null);
    try {
      const page = await load(next);
      setOlder((cur) => [...cur, ...page.entries]);
      setCursor(page.nextCursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the history.');
    } finally {
      setBusy(false);
    }
  };
  const seen = new Set<string>();
  const entries = [...first.entries, ...older].filter((e) => {
    const k = `${e.kind}-${e.id}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return { entries, hasMore: next !== null, showOlder, busy, error };
}

function Detail({ leadId, api, listKey, approvalsHref, onBack }: { leadId: string; api: LeadSplitApi; listKey: readonly unknown[]; approvalsHref: string | null; onBack: () => void }) {
  const qc = useQueryClient();
  const detailKey = [...listKey, 'detail', leadId];
  const q = useQuery({
    queryKey: detailKey,
    queryFn: () => api.detail(leadId),
    placeholderData: keepPreviousData,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: [...listKey] });
  const flag = useMutation({ mutationFn: (on: boolean) => api.setNeedsPerson(leadId, on), onSuccess: refresh });

  // keepPreviousData would show the PREVIOUS lead while a new one loads: only ever render data that is this lead's.
  const d = q.data && q.data.id === leadId ? q.data : null;
  if (q.error && !d) {
    return (
      <div className="space-y-3 p-4" data-testid="lead-detail-error">
        <Button type="button" variant="ghost" size="sm" className="lg:hidden" onClick={onBack}>Back to leads</Button>
        <p role="alert" className="kc-body text-sm text-destructive">We could not open this lead. It may have been removed, or you may not have access to it.</p>
      </div>
    );
  }
  if (!d) {
    return (
      <div className="flex items-center justify-center p-10" data-testid="lead-detail-loading">
        <Spinner />
      </div>
    );
  }
  return <DetailBody d={d} api={api} approvalsHref={approvalsHref} onBack={onBack} refresh={refresh} onFlag={(on) => flag.mutate(on)} flagging={flag.isPending} />;
}

export function DetailBody({ d, api, approvalsHref, onBack, refresh, onFlag, flagging }: { d: LeadSplitDetail; api: LeadSplitApi; approvalsHref: string | null; onBack: () => void; refresh: () => void; onFlag: (on: boolean) => void; flagging: boolean }) {
  const stage = stageOf({ outcome: d.outcome, outcomeSetBy: d.outcomeSetBy, needsPerson: d.needsPerson, contactStatus: d.contactStatus });
  const response = firstResponseSentence(d.firstResponseVia, d.dialledAfterSeconds);
  const story = useStoryEntries(d.history, (cursor) => api.detail(d.id, cursor).then((x) => x.history));
  const [callOpen, setCallOpen] = useState(false);
  const callState = d.contact.call;
  const textState = d.contact.text;
  // The text composer: shown for a club with AI SMS, DISABLED WITH ITS REASON when something stops it right now (the server refuses again at
  // the action). One, under the newest conversation, or on its own when the lead has no conversation yet.
  const composer = textState ? <TextLeadComposer key={d.id} leadId={d.id} firstName={firstNameOf(d.name)} api={api} paused={d.sam.paused} onSent={refresh} disabledReason={textState.enabled ? null : textState.message} demo={d.demo === true} /> : undefined;
  const focusComposer = () => {
    const box = typeof document !== 'undefined' ? document.getElementById(`text-lead-${d.id}`) : null;
    box?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    box?.focus?.();
  };
  const hasConversation = story.entries.some((e) => e.kind === 'conversation');
  const hasProfile = Boolean(d.profile.goal || d.profile.motivation || d.profile.preferredTimes || d.profile.objections);
  return (
    <div className="space-y-4 p-4" data-testid="lead-detail">
      <Button type="button" variant="ghost" size="sm" className="lg:hidden" onClick={onBack} data-testid="lead-back">
        Back to leads
      </Button>

      {/* 1. THE HEADER CARD (the ink surface in the portal look). */}
      <header className="kc-header kc-card space-y-3 rounded border border-border bg-card p-4" data-testid="lead-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span aria-hidden data-stage={stage} style={{ '--kc-stage-fill': STAGE_STYLES[stage].portalFill ?? 'transparent' } as React.CSSProperties} className="kc-avatar flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-foreground text-sm font-semibold text-background">
              {initialsOf(d.name)}
            </span>
            <div className="min-w-0 space-y-1">
              <h2 className="kc-display truncate text-xl font-semibold text-foreground" data-testid="lead-name">{d.name ?? 'Unnamed lead'}{d.demo === true && <span className="ml-2 rounded border px-1 align-middle text-xs font-normal text-muted-foreground" data-testid="lead-detail-seeded-badge" title="Synthetic demo data, not a real enquiry">Demo data</span>}{d.isTest && <span className="ml-2 rounded border px-1 align-middle text-xs font-normal text-muted-foreground" data-testid="lead-detail-test-badge">Test</span>}</h2>
              <p className="flex flex-wrap items-center gap-2">
                <StageChip stage={stage} testId="lead-stage-detail" />
                <TemperatureDot quality={d.quality} testId="lead-quality-detail" />
              </p>
              <p className="kc-body truncate text-sm text-muted-foreground">
                {[d.accountName, d.campaign].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-start gap-2" data-testid="lead-actions">
            {callState && (
              <span className="flex flex-col items-start gap-1">
                <Button type="button" size="sm" className="kc-save gap-1.5" disabled={!callState.enabled} aria-expanded={callOpen} onClick={() => setCallOpen(!callOpen)} title={callState.message ?? undefined} data-testid="lead-call-button">
                  <Phone size={15} strokeWidth={1.5} aria-hidden />
                  Call {firstNameOf(d.name)}
                </Button>
                {!callState.enabled && <span className="kc-reason max-w-[11rem] text-xs text-muted-foreground" data-testid="lead-call-reason">{callState.message}</span>}
              </span>
            )}
            {textState && (
              <span className="flex flex-col items-start gap-1">
                <Button type="button" variant="outline" size="sm" className="kc-text-button gap-1.5" disabled={!textState.enabled} onClick={focusComposer} title={textState.message ?? undefined} data-testid="lead-text-button">
                  <MessageSquare size={15} strokeWidth={1.5} aria-hidden />
                  Text {firstNameOf(d.name)}
                </Button>
                {!textState.enabled && <span className="kc-reason max-w-[11rem] text-xs text-muted-foreground" data-testid="lead-text-reason">{textState.message}</span>}
              </span>
            )}
            <Button type="button" variant={d.needsPerson ? 'default' : 'outline'} size="sm" aria-pressed={d.needsPerson} disabled={flagging} onClick={() => onFlag(!d.needsPerson)} data-testid="lead-needs-person-toggle">
              {d.needsPerson ? 'Needs a person: on' : 'Needs a person'}
            </Button>
          </div>
        </div>
        <dl className="kc-body grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-4" data-testid="lead-facts">
          <div><dt className="kc-label text-xs text-muted-foreground">Enquired</dt><dd className="text-foreground" data-testid="lead-enquired">{when(d.receivedAt)}</dd></div>
          <div>
            <dt className="kc-label text-xs text-muted-foreground">First response</dt>
            <dd className="text-foreground" data-testid={response ? 'lead-dialled' : 'lead-contact'}>
              {response ? <>{response.verb} <span className="kc-green-num font-semibold text-green-700">{response.time}</span></> : d.relayOnly && d.contactStatus === 'not_contacted' ? SENT_TO_CRM_LABEL : 'Not yet'}
            </dd>
          </div>
          <div><dt className="kc-label text-xs text-muted-foreground">Came from</dt><dd className="text-foreground" data-testid="lead-source">{sourceLabel(d.source)}</dd></div>
          <div>
            <dt className="kc-label text-xs text-muted-foreground">Phone</dt>
            <dd className="text-foreground">
              {/* ⚠️ A demo lead's number (or any number in ACMA's fiction ranges) is PLAIN TEXT: nothing here can dial it. */}
              {canDialLead(d) ? (
                <a className="underline" href={`tel:${d.phone}`} data-testid="lead-phone">{d.phone}</a>
              ) : (
                <span data-testid="lead-phone">{d.phone}</span>
              )}
            </dd>
          </div>
        </dl>
      </header>

      {callOpen && callState?.enabled && (
        <CallLeadPanel leadId={d.id} firstName={firstNameOf(d.name)} api={api} onClose={() => setCallOpen(false)} onStarted={refresh} demo={d.demo === true} />
      )}

      <LeadNoticesBlock d={d} />

      {d.pendingDraftCount > 0 && (
        <p className="kc-body rounded border border-border px-3 py-2 text-sm text-foreground" data-testid="lead-draft-badge">
          {approvalsHref ? (
            <>
              {d.pendingDraftCount} text draft{d.pendingDraftCount === 1 ? '' : 's'} waiting. <Link className="underline" to={approvalsHref}>Open in Approvals</Link>
            </>
          ) : (
            'A text is waiting for Kyck to approve.'
          )}
        </p>
      )}

      {/* 2. WHERE IT'S AT: one sentence, the ONE outcome control, and the AI suggestion as "Confirm?". */}
      <section className="kc-card space-y-2 rounded border border-border bg-card p-3" aria-label="Where it's at" data-testid="lead-outcome-bar">
        <p className="kc-body text-sm text-foreground" data-testid="where-its-at">
          {whereItsAt({ outcome: d.outcome, setBy: d.outcomeSetBy, confirmedBy: d.outcomeConfirmedBy, source: d.outcomeSource, setAt: d.outcomeSetAt })}
        </p>
        <LeadOutcomePicker
          key={`${d.id}-${d.outcome}-${d.outcomeSetBy ?? ''}`}
          id={`lead-outcome-${d.id}`}
          current={d.outcome}
          setBy={d.outcomeSetBy}
          sourceNote={d.outcomeSuggestion?.confidence ? `${d.outcomeSuggestion.confidence} confidence` : ''}
          onSave={async (code) => {
            await api.setOutcome(d.id, code);
            refresh();
          }}
        />
      </section>

      {/* 3. THE SAM STRIP (only when the club has AI SMS). */}
      <SamStrip d={d} api={api} refresh={refresh} />

      {/* 4. TWO COLUMNS: the story so far, and what we know. */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_260px]">
        <section aria-label="The story so far" className="kc-card min-w-0 space-y-3 rounded border border-border bg-card p-4" data-testid="lead-history">
          <h3 className="kc-h text-xl font-semibold text-foreground">The story so far</h3>
          {story.entries.length === 0 ? (
            <p className="kc-body text-sm text-muted-foreground">Nothing recorded for this lead yet.</p>
          ) : (
            <LeadStory entries={story.entries} assistantName={d.assistantName} leadName={d.name} loadPlayUrl={api.loadPlayUrl} composer={composer} timeZone={d.timezone} />
          )}
          {composer && !hasConversation && <div data-testid="text-composer-standalone">{composer}</div>}
          {story.error && <p role="alert" className="text-sm text-destructive">{story.error}</p>}
          {story.hasMore && <Button type="button" variant="outline" size="sm" onClick={() => void story.showOlder()} disabled={story.busy}>{story.busy ? 'Loading…' : 'Show older'}</Button>}
        </section>
        <aside aria-label="What we know" className="kc-card space-y-3 self-start rounded border border-border bg-card p-3" data-testid="lead-know">
          <h3 className="kc-h text-xl font-semibold text-foreground">What we know</h3>
          {hasProfile ? (
            <dl className="kc-body space-y-2 text-sm" data-testid="lead-profile">
              {d.profile.goal && <div><dt className="text-muted-foreground">Goal</dt><dd className="text-foreground">{d.profile.goal}</dd></div>}
              {d.profile.motivation && <div><dt className="text-muted-foreground">Why now</dt><dd className="text-foreground">{d.profile.motivation}</dd></div>}
              {d.profile.preferredTimes && <div><dt className="text-muted-foreground">Best time</dt><dd className="text-foreground">{d.profile.preferredTimes}</dd></div>}
              {d.profile.objections && <div><dt className="text-muted-foreground">Worries</dt><dd className="text-foreground">{d.profile.objections}</dd></div>}
            </dl>
          ) : (
            <p className="kc-body text-sm text-muted-foreground" data-testid="lead-know-empty">Nothing yet. This fills in as they talk to us.</p>
          )}
          <dl className="kc-body space-y-1 border-t border-border pt-2 text-sm" aria-label="Contact">
            <div><dt className="text-muted-foreground">Phone</dt><dd className="text-foreground" data-testid="know-phone">{d.phone}</dd></div>
            {d.email && <div><dt className="text-muted-foreground">Email</dt><dd className="break-all text-foreground" data-testid="know-email">{d.email}</dd></div>}
          </dl>
        </aside>
      </div>
    </div>
  );
}

// ── the view ────────────────────────────────────────────────────────────────────────────────────────────────

export function LeadSplitView({ api, queryKey, accountId = null, clubs: clubsProp, staffClubs, showTestToggle = false, approvalsHref = null, initialStage = 'all', scopeLabel = null, initialFiltersOpen = false }: LeadSplitViewProps) {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('lead');
  const [stage, setStage] = useState<LeadStageTab>(initialStage);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, SEARCH_DEBOUNCE_MS);
  const [period, setPeriod] = useState<SplitPeriodId>('30');
  // The window start is read ONCE per period choice, so a refetch does not drift the window under a reader.
  const [range, setRange] = useState<{ receivedFrom?: string; receivedTo?: string }>(() => splitRangeFor('30', Date.now()));
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [quality, setQuality] = useState<LeadQuality | ''>('');
  const [source, setSource] = useState('');
  const [reachedBy, setReachedBy] = useState<ReachedBy | ''>('');
  const [club, setClub] = useState('');
  const [brandId, setBrandId] = useState('');
  const [groupId, setGroupId] = useState('');
  // Staff only: the Brand / Group choice narrows the Club list (and the server narrows the scope the same way).
  const clubs = staffClubs ? clubsMatching(staffClubs, { brandId: brandId || undefined, groupId: groupId || undefined }).map((c) => ({ id: c.id, name: c.demo ? `${c.name} (Demo)` : c.name })) : clubsProp;
  const [includeTest, setIncludeTest] = useState(false);

  const scopeId = accountId ?? (club || null);
  const filters: LeadSplitFilters = {
    ...(debouncedSearch.trim() ? { q: debouncedSearch.trim() } : {}),
    ...range,
    ...(quality ? { quality: [quality] } : {}),
    ...(source ? { source } : {}),
    ...(reachedBy ? { reachedBy } : {}),
    stage,
    ...(includeTest ? { includeTest: true } : {}),
    ...(staffClubs && brandId ? { brandId } : {}),
    ...(staffClubs && groupId ? { groupId } : {}),
  };
  const listKey = [...queryKey, 'split', scopeId ?? 'all'];
  const list = useInfiniteQuery({
    queryKey: [...listKey, 'list', JSON.stringify(filters)],
    queryFn: ({ pageParam }) => api.list(filters, pageParam, scopeId),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const pages = list.data?.pages ?? [];
  const rows = pages.flatMap((p) => p.leads);
  const counts = pages[0]?.counts ?? null;
  const sources = pages[0]?.sources ?? [];
  const capped = pages.some((p) => p.scanCapped);
  const nowMs = Date.now();
  const filtersActive = Boolean(debouncedSearch.trim() || quality || source || reachedBy || period === 'custom' || includeTest || brandId || groupId);

  const select = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('lead', id);
    else next.delete('lead');
    setParams(next, { replace: true });
  };

  const choosePeriod = (id: SplitPeriodId) => {
    setPeriod(id);
    if (id === 'custom') setRange({});
    else setRange(splitRangeFor(id, Date.now()));
  };
  const applyCustom = (from: string, to: string) => {
    setRange({
      ...(from ? { receivedFrom: new Date(`${from}T00:00:00`).toISOString() } : {}),
      ...(to ? { receivedTo: new Date(`${to}T23:59:59.999`).toISOString() } : {}),
    });
  };
  const clearFilters = () => {
    setSearch('');
    setQuality('');
    setSource('');
    setReachedBy('');
    setIncludeTest(false);
    setBrandId('');
    setGroupId('');
    setClub('');
    setCustomFrom('');
    setCustomTo('');
    choosePeriod('30');
  };

  const [filtersOpen, setFiltersOpen] = useState(initialFiltersOpen);
  const extraActive = Boolean(source || quality || reachedBy || includeTest || brandId || groupId);
  const scopeName = accountId ? (clubsProp?.find((c) => c.id === accountId)?.name ?? scopeLabel ?? null) : club ? (clubs?.find((c) => c.id === club)?.name ?? null) : null;
  const periodLabel = SPLIT_PERIODS.find((p) => p.id === period)?.label ?? '';
  // The stage tabs' dots, from the ONE colour map (the tab for "all" has none).
  const TAB_DOT: Partial<Record<LeadStageTab, LeadStageKey>> = { needs_team: 'needs_you', not_contacted: 'new', in_conversation: 'talking', booked: 'booked', joined: 'joined', not_interested: 'not_interested' };

  return (
    <div className="kc-root space-y-3" data-testid="lead-split">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="kc-display text-2xl font-semibold text-foreground" data-testid="leads-title">Leads</h2>
          <p className="kc-body text-sm text-muted-foreground" data-testid="leads-subtitle">{scopeName ?? scopeLabel ?? 'All your clubs'} · {periodLabel}</p>
        </div>
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => e.preventDefault()} aria-label="Lead filters">
          <label className="space-y-1">
            <span className="sr-only">Search</span>
            <input className={`${field} w-56`} type="search" value={search} maxLength={100} placeholder="Search name, phone or email" onChange={(e) => setSearch(e.target.value)} aria-label="Search leads" />
          </label>
          {!accountId && clubs && clubs.length > 1 && (
            <label className="space-y-1">
              <span className="sr-only">Club</span>
              <select className={field} value={club} onChange={(e) => { setClub(e.target.value); select(null); }} aria-label="Club">
                <option value="">All clubs</option>
                {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )}
          <label className="space-y-1">
            <span className="sr-only">Period</span>
            <select className={field} value={period} onChange={(e) => choosePeriod(e.target.value as SplitPeriodId)} aria-label="Period">
              {SPLIT_PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
          {period === 'custom' && (
            <>
              <label className="space-y-1">
                <span className={label}>From</span>
                <input type="date" className={field} value={customFrom} onChange={(e) => { setCustomFrom(e.target.value); applyCustom(e.target.value, customTo); }} />
              </label>
              <label className="space-y-1">
                <span className={label}>To</span>
                <input type="date" className={field} value={customTo} onChange={(e) => { setCustomTo(e.target.value); applyCustom(customFrom, e.target.value); }} />
              </label>
            </>
          )}
          <Button type="button" variant="outline" size="sm" aria-expanded={filtersOpen} aria-controls="lead-more-filters" onClick={() => setFiltersOpen(!filtersOpen)} data-testid="filters-button">
            Filters{extraActive ? ' (on)' : ''}
          </Button>
          {filtersActive && <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>}
        </form>
      </div>

      {filtersOpen && (
        <div id="lead-more-filters" className="kc-card flex flex-wrap items-end gap-3 rounded border border-border bg-card p-3" data-testid="more-filters">
          <label className="space-y-1">
            <span className={label}>Source</span>
            <select className={field} value={source} onChange={(e) => setSource(e.target.value)} aria-label="Source">
              <option value="">Any</option>
              {sources.map((s) => <option key={s} value={s}>{sourceLabel(s)}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className={label}>Lead quality</span>
            <select className={field} value={quality} onChange={(e) => setQuality(e.target.value as LeadQuality | '')} aria-label="Lead quality">
              <option value="">Any</option>
              {LEAD_QUALITIES.map((qk) => <option key={qk} value={qk}>{LEAD_QUALITY_LABELS[qk]}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className={label}>Reached by</span>
            <select className={field} value={reachedBy} onChange={(e) => setReachedBy(e.target.value as ReachedBy | '')} aria-label="Reached by">
              <option value="">Any</option>
              {REACHED_BY.map((r) => <option key={r} value={r}>{r === 'sam' ? 'AI SMS' : r === 'call' ? 'Call' : 'Not yet'}</option>)}
            </select>
          </label>
          {!accountId && staffClubs && (
            <>
              <label className="space-y-1">
                <span className={label}>Brand</span>
                <select className={field} value={brandId} onChange={(e) => { setBrandId(e.target.value); setClub(''); select(null); }} aria-label="Brand">
                  <option value="">All brands</option>
                  {scopeOptions(staffClubs, 'brand').map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </label>
              <label className="space-y-1">
                <span className={label}>Group</span>
                <select className={field} value={groupId} onChange={(e) => { setGroupId(e.target.value); setClub(''); select(null); }} aria-label="Group">
                  <option value="">All groups</option>
                  {scopeOptions(staffClubs, 'group').map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </label>
            </>
          )}
          {showTestToggle && (
            <label className="flex items-center gap-1.5 pb-1 text-sm text-foreground">
              <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} />
              Show test leads
            </label>
          )}
        </div>
      )}

      <div role="tablist" aria-label="Lead stage" className="flex flex-wrap gap-1" data-testid="stage-tabs">
        {LEAD_STAGE_TABS.map((t) => {
          const dot = TAB_DOT[t];
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={stage === t}
              data-testid={`stage-${t}`}
              // The "Needs you" tab is the ONE volt element on the page (the portal look paints it; the staff look keeps its amber outline).
              data-volt={t === 'needs_team' ? 'true' : undefined}
              onClick={() => { setStage(t); select(null); }}
              className={`kc-tab inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm ${stage === t ? 'border-foreground bg-foreground text-background' : t === 'needs_team' ? 'border-amber-400 text-foreground hover:bg-muted/50' : 'border-border text-foreground hover:bg-muted/50'}`}
            >
              {dot && <span aria-hidden className="kc-tab-dot inline-block h-2 w-2 rounded-full" data-stage={dot} style={{ '--kc-stage-fill': STAGE_STYLES[dot].portalFill ?? 'transparent' } as React.CSSProperties} />}
              {LEAD_STAGE_LABELS[t]}
              <span className="opacity-70" data-testid={`stage-count-${t}`}>{counts ? counts[t] : '…'}</span>
            </button>
          );
        })}
      </div>

      {capped && (
        <p className="kc-body text-xs text-muted-foreground" data-testid="leads-capped">
          This period holds more leads than we can count at once, so the numbers are a minimum. Narrow the dates to see them all.
        </p>
      )}

      <div className="grid gap-3 lg:grid-cols-[minmax(340px,420px)_1fr]">
        <div className={`kc-card overflow-hidden rounded border border-border bg-card ${selectedId ? 'hidden lg:block' : 'block'}`}>
          <div className="max-h-[70vh] overflow-y-auto lg:max-h-[calc(100dvh-17rem)]" data-testid="lead-list">
            {list.isLoading && <div className="flex justify-center p-8"><Spinner /></div>}
            {list.error && !list.data && <p role="alert" className="kc-body p-4 text-sm text-destructive">We could not load the leads just now. Try again in a moment.</p>}
            {list.data && rows.length === 0 && <p className="kc-body p-4 text-sm text-muted-foreground" data-testid="leads-empty">{emptyListMessage(filtersActive, stage)}</p>}
            <ul aria-label="Leads">
              {rows.map((r) => <ListRow key={r.id} row={r} selected={r.id === selectedId} onSelect={() => select(r.id)} nowMs={nowMs} />)}
            </ul>
            {list.hasNextPage && (
              <div className="p-3">
                <Button type="button" variant="outline" size="sm" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
                  {list.isFetchingNextPage ? 'Loading…' : 'Show more'}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className={`kc-card min-w-0 overflow-hidden rounded border border-border bg-card ${selectedId ? 'block' : 'hidden lg:block'}`}>
          <div className="max-h-[calc(100dvh-8rem)] overflow-y-auto lg:max-h-[calc(100dvh-17rem)]">
            {selectedId ? (
              <Detail leadId={selectedId} api={api} listKey={listKey} approvalsHref={approvalsHref} onBack={() => select(null)} />
            ) : (
              <p className="kc-body p-8 text-sm text-muted-foreground" data-testid="lead-detail-empty">Choose a lead to see the conversation, the calls and the outcome.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
